import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ThumbsUp, ChatCircle, Trash, PaperPlaneRight, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(s)) return "";
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return m + "m";
  const h = Math.floor(m / 60); if (h < 24) return h + "h";
  const d = Math.floor(h / 24); if (d < 7) return d + "d";
  return new Date(iso).toLocaleDateString("en", { day: "numeric", month: "short" });
}

export default function EventPhotoLightbox({ photos, index, onClose, onChange, onNav }) {
  const { user } = useAuth();
  const photo = photos[index];
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const loadComments = () => {
    if (!photo) return;
    api.get(`/public/events/photos/${photo.id}/comments`).then((r) => setComments(r.data.comments || [])).catch(() => setComments([]));
  };
  useEffect(() => { loadComments(); /* eslint-disable-next-line */ }, [photo?.id]);
  if (!photo) return null;

  const like = async () => {
    if (!user) return toast.error("Sign in to like");
    try { await api.post(`/events/photos/${photo.id}/like`); onChange?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const postComment = async () => {
    if (!text.trim()) return;
    try { await api.post(`/events/photos/${photo.id}/comments`, { text }); setText(""); loadComments(); onChange?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delComment = async (cid) => {
    try { await api.delete(`/events/photos/comments/${cid}`); loadComments(); onChange?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl overflow-hidden p-0" data-testid="photo-lightbox">
        <DialogTitle className="sr-only">Event photo</DialogTitle>
        <div className="grid md:grid-cols-[1.5fr_1fr]">
          <div className="relative flex items-center justify-center bg-black">
            <img src={fileUrl(photo.file_id)} alt={photo.caption || "Event photo"} className="max-h-[80vh] w-full object-contain" />
            {index > 0 && <button onClick={() => onNav(index - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70" data-testid="photo-prev"><CaretLeft size={20} /></button>}
            {index < photos.length - 1 && <button onClick={() => onNav(index + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70" data-testid="photo-next"><CaretRight size={20} /></button>}
            <button onClick={onClose} className="absolute right-2 top-2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 md:hidden"><X size={18} /></button>
          </div>
          <div className="flex max-h-[80vh] flex-col">
            <div className="border-b border-border/60 p-4">
              {photo.uploader_name && <p className="text-sm text-muted-foreground">Added by <span className="font-medium text-foreground">{photo.uploader_name}</span></p>}
              {photo.caption && <p className="mt-1 text-sm">{photo.caption}</p>}
              <div className="mt-2 flex items-center gap-4 text-sm">
                <button onClick={like} className={`flex items-center gap-1.5 font-medium hover:text-secondary ${photo.my_like ? "text-secondary" : "text-muted-foreground"}`} data-testid="photo-like-btn">
                  <ThumbsUp size={16} weight={photo.my_like ? "fill" : "regular"} /> {photo.like_count || 0}
                </button>
                <span className="flex items-center gap-1.5 text-muted-foreground"><ChatCircle size={16} /> {comments.length}</span>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="photo-comments">
              {comments.length === 0 && <p className="text-sm text-muted-foreground">No comments yet — be the first!</p>}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2.5" data-testid={`photo-comment-${c.id}`}>
                  {c.avatar_file_id ? <img src={fileUrl(c.avatar_file_id)} alt={c.user_name} className="h-8 w-8 shrink-0 rounded-full object-cover" /> : <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-xs">{(c.user_name || "?")[0]}</span>}
                  <div className="min-w-0 flex-1">
                    <div className="rounded-2xl bg-muted/60 px-3 py-1.5">
                      <div className="text-xs font-medium">{c.user_name}</div>
                      <div className="text-sm">{c.text}</div>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 pl-1 text-xs text-muted-foreground">
                      <span>{timeAgo(c.created_at)}</span>
                      {user && (user.id === c.user_id || user.role === "admin") && <button onClick={() => delComment(c.id)} className="hover:text-destructive"><Trash size={12} /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {user ? (
              <div className="flex gap-2 border-t border-border/60 p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Write a comment…" className="rounded-full" data-testid="photo-comment-input" />
                <Button size="icon" className="shrink-0 rounded-full" onClick={postComment} data-testid="photo-comment-send"><PaperPlaneRight size={16} /></Button>
              </div>
            ) : (
              <div className="border-t border-border/60 p-3 text-center text-xs text-muted-foreground"><Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link> to like and comment</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
